import { getCustomValidation } from "./customValidations";
import { parse, format, isValid } from "date-fns";
import type { Field, Validation, UseFormOptions } from "./types";

export const getValidationsObject = (
  field: Field,
  getValues?: UseFormOptions["getValues"]
): Record<string, any> => {
  let res: Record<string, any> = {
    valueAsNumber: field.type === "number",
    value: field.value || undefined,
  };

  if (field.validations) {
    field.validations.forEach((v) => {
      const validation =
        v.type === "custom"
          ? { validate: { ...res?.validate, ...getValidation(v, getValues) } }
          : getValidation(v, getValues);

      res = {
        ...res,
        ...validation,
      };
    });
  }
  return res;
};

export const getValidation = (
  validation: Validation,
  getValues?: UseFormOptions["getValues"]
): Record<string, any> => {
  switch (validation.type) {
    case "required":
      return getRule(validation);
    case "min":
    case "max":
    case "minLength":
    case "maxLength":
      return getValueRule(validation as Validation & { value: number });
    case "email":
      return getEmail(validation);
    case "phoneNumber":
      return getPhoneNumber(validation);
    case "regex":
      return getRegex(validation as Validation & { value: string });
    case "custom":
      return getCustomValidation(validation as Validation & { customName: string }, getValues);
    default:
      return {};
  }
};

function getRule(validation: Validation): Record<string, string> {
  return {
    [validation.type]: validation.message,
  };
}

function getValueRule(validation: Validation & { value: number }): Record<string, { message: string; value: number }> {
  return {
    [validation.type]: {
      message: validation.message,
      value: validation.value,
    },
  };
}

function getEmail(validation: Validation): Record<string, { value: RegExp; message: string }> {
  return {
    pattern: {
      value:
        /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@([a-zA-Z0-9-]+\.)+[a-zA-Z0-9-]{2,}$/,
      message: validation.message,
    },
  };
}

function getPhoneNumber(validation: Validation): Record<string, { value: RegExp; message: string }> {
  return {
    pattern: {
      value: /^07[0-9]{8}$/,
      message: validation.message,
    },
  };
}

function getRegex(validation: Validation & { value: string }): Record<string, { value: RegExp; message: string }> {
  return {
    pattern: {
      value: new RegExp(validation.value),
      message: validation.message,
    },
  };
}

export const getDateValue = (value: string | number | Date | undefined): Date | string | undefined => {
  if (!value) return undefined;

  // Try to parse various date formats
  const dateFormats = [
    "dd/MM/yyyy",
    "dd-MM-yyyy",
    "dd.MM.yyyy",
    "MM/dd/yyyy",
    "MM-dd-yyyy",
    "MM.dd.yyyy",
    "yyyy/MM/dd",
    "yyyy-MM-dd",
    "yyyy.MM.dd",
    "dd/MM/yy",
    "dd-MM-yy",
    "dd.MM.yy",
  ];

  const stringValue = typeof value === "string" ? value : value.toString();

  for (const dateFormat of dateFormats) {
    try {
      const parsedDate = parse(stringValue, dateFormat, new Date());
      if (isValid(parsedDate)) {
        return format(parsedDate, "yyyy-MM-dd");
      }
    } catch (e) {
      // Continue to next format
    }
  }

  return new Date(value);
};
